import { isPdfMime, isVideoMime } from '@avslack/shared';
import { loadPdfJs } from './pdf';

/**
 * ELONEZETI KEP a feltoltes kozben - PDF elso oldala, video egy kockaja
 * (versenyaudit K3).
 *
 * A feltolto bongeszoje a fajlt mar a kezeben tartja, es meg is tudja
 * jeleniteni - igy a szervernek nem kell ffmpeg, sem PDF-renderelo. A kepet
 * a szerver ellenorzi es atmeretezi (`POST /api/files/:id/preview`).
 *
 * Minden hiba csendes: elonezet nelkul is hasznalhato a fajl, csak egy
 * ikonnal jelenik meg. Egy feltoltest egy elonezet miatt nem allitunk meg.
 */

/** A kep hosszabbik ele. A szerver 640-re kicsinyit; ennyi eleg az eleshez. */
const MAX_EDGE = 1280;
/**
 * Ennel tovabb nem varunk elonezetre - a feltoltes ne alljon miatta.
 *
 * Hatterben levo fulon a bongeszo nem futtat `requestAnimationFrame`-et, a
 * pdf.js pedig abban rajzol: idokorlat nelkul az a feltoltes, ami kozben a
 * felhasznalo masik fulre valtott, orokre "folyamatban" maradna.
 */
const PREVIEW_TIMEOUT_MS = 8000;
/** Ennel nagyobb PDF-et nem toltunk be a memoriaba csak egy elonezetert. */
const MAX_PDF_BYTES = 60 * 1024 * 1024;

export function wantsPreview(mime: string): boolean {
  return isPdfMime(mime) || isVideoMime(mime);
}

export async function makePreview(file: File): Promise<Blob | null> {
  const work = isPdfMime(file.type)
    ? pdfFirstPage(file)
    : isVideoMime(file.type)
      ? videoFrame(file)
      : Promise.resolve(null);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), PREVIEW_TIMEOUT_MS);
  });
  try {
    return await Promise.race([work, timeout]);
  } catch {
    // Lasd fent: az elonezet hianya nem hiba.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85));
}

async function pdfFirstPage(file: File): Promise<Blob | null> {
  if (file.size > MAX_PDF_BYTES) return null;
  const pdfjs = await loadPdfJs();
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  const document = await task.promise;
  try {
    const page = await document.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(MAX_EDGE / Math.max(base.width, base.height), 2);
    const viewport = page.getViewport({ scale });

    const canvas = window.document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    // Feher lap: az atlatszo PDF-hatter JPEG-ben feketenek latszana.
    await page.render({ canvas, viewport, background: '#ffffff' }).promise;
    return await canvasToJpeg(canvas);
  } finally {
    void task.destroy();
  }
}

function videoFrame(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const finish = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(blob);
    };
    const timer = setTimeout(() => finish(null), PREVIEW_TIMEOUT_MS);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => finish(null);
    video.onloadedmetadata = () => {
      // Nem az elso kocka: az sokszor fekete (attunes). Egy masodperc, de
      // rovid videonal legfeljebb a hossz tizede.
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      video.currentTime = Math.min(1, duration * 0.1);
    };
    video.onseeked = () => {
      if (!video.videoWidth || !video.videoHeight) return finish(null);
      const scale = Math.min(1, MAX_EDGE / Math.max(video.videoWidth, video.videoHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
      void canvasToJpeg(canvas).then(finish);
    };
    video.src = url;
  });
}
